import { useState, useCallback, useEffect } from 'react';
import throttle from 'lodash.throttle';
import isEqual from 'lodash.isequal';

const ON_CHANGE_ACTIONS = {
  selectOption: 'select-option',
  createOption: 'create-option',
  clear: 'clear',
};

const ON_CHANGE_ACTIONS_ARRAY = Object.values(ON_CHANGE_ACTIONS);

const MENU_LIST_CLASSNAME = 'ws-ui-base-select__menu-list';

const THROTTLE_VALUE = 500;

const useSelect = ({
  defaultInputValue,
  isAutocomplete,
  isSearchable,
  onBlur,
  onFocus,
  onChange,
  onCreateOption,
  onInputChange,
  preventOverflow,
  defaultMenuIsOpen = false,
  controlledInputValue,
  openMenuOnFocus,
  menuIsOpen: controlledIsOpen,
  withDropdownIndicator,
}) => {
  const [isOpen, setIsOpen] = useState(defaultMenuIsOpen);

  const initializeInitialValue = () => {
    if (isAutocomplete) {
      if (defaultInputValue) {
        const createOption = onCreateOption || ((label) => ({ label }));

        return createOption(defaultInputValue);
      }

      return null;
    }

    return undefined;
  };

  const [value, setValue] = useState(initializeInitialValue);
  const [onChangeCallbackData, setOnChangeCallbackData] = useState(initializeInitialValue);
  const [inputValue, setInputValue] = useState(defaultInputValue || '');
  const [isBlurred, setIsBlurred] = useState(false);

  const onValueChange = useCallback(
    (newValue, options = {}) => {
      if (!isEqual(onChangeCallbackData, newValue) || options.forceChange) {
        onChange(newValue);
        setOnChangeCallbackData(newValue);
      }
    },
    [onChangeCallbackData, onChange]
  );

  const handleChange = (data, { action }) => {
    if (ON_CHANGE_ACTIONS_ARRAY.includes(action)) {
      onValueChange(data, { forceChange: isAutocomplete });
      setValue(data);

      if (isAutocomplete) {
        const inpValue = data?.label || '';
        setInputValue(inpValue);
      }
    }
  };

  const handleClear = () => {
    if (isAutocomplete) {
      setValue(null);
      setIsOpen(defaultMenuIsOpen);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    setIsBlurred(true);
  };

  const handleFocus = () => {
    if (openMenuOnFocus && !isOpen) {
      setIsOpen(true);
    }

    onFocus();
  };

  // we need such effect in order to trigger on change with updated value
  // after onChange which will happened in handleChange callback
  useEffect(() => {
    if (isBlurred) {
      setIsBlurred(false);

      onValueChange(value);
      onBlur();
    }
  }, [isBlurred, onBlur, onChange, value, onValueChange]);

  useEffect(() => {
    if (controlledInputValue != null && controlledInputValue !== inputValue && isAutocomplete) {
      handleInputChange(controlledInputValue, { action: 'input-change' });
    }
  }, [controlledInputValue, inputValue]);

  const handleInputChange = (inpValue, { action }) => {
    const isInputChangeAction = action === 'input-change';

    if (isInputChangeAction && isAutocomplete) {
      setInputValue(inpValue);
      setIsOpen(!!inpValue || defaultMenuIsOpen);

      if (inpValue === '') {
        setValue(null);
      } else {
        const handler = onCreateOption || ((label) => ({ label }));
        const data = handler(inpValue);
        setValue(data);
      }
    }

    if (isInputChangeAction && isSearchable) {
      onInputChange(inpValue);
    }
  };

  const handleOpen = () => {
    if (withDropdownIndicator) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (!controlledIsOpen && controlledIsOpen !== null) {
      setIsOpen(false);
    }
  }, [controlledIsOpen]);

  const handleClose = () => {
    if (controlledIsOpen === null) {
      setIsOpen(false);
    }
  };

  const handleCloseOnScroll = (event) => {
    if (preventOverflow && !event?.target?.classList?.contains(MENU_LIST_CLASSNAME)) {
      setIsOpen(false);
    }
  };

  const throttledHandleCloseOnScroll = useCallback(throttle(handleCloseOnScroll, THROTTLE_VALUE), [
    preventOverflow,
  ]);

  return {
    isOpen,
    value,
    inputValue,
    throttledHandleCloseOnScroll,
    handleBlur,
    handleFocus,
    handleClear,
    handleChange,
    handleClose,
    handleInputChange,
    handleOpen,
  };
};

export default useSelect;
